import React, { useState, useEffect, useMemo } from 'react';
import { TrendingUp, Clock, Sparkles, ArrowRight, Instagram, Facebook, Twitter, Linkedin } from 'lucide-react';
import PostCard from './PostCard';
import { postsService } from '../services/postsService';
import StoriesSection from './StoriesSection';
import { adsDisplayService } from '../services/adsDisplayService';
import { supabase } from '../lib/supabase';
import { ArtCategory } from '../types';
import { useLanguage } from '../context/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

interface HomeViewProps {
  onNavigate?: (tab: string) => void;
}

const HomeView: React.FC<HomeViewProps> = () => {
  const navigate = useNavigate();
  const { select } = useLanguage();
  const { user } = useAuth();
  const [posts, setPosts] = useState<Record<string, unknown>[]>([]);
  const [trendingAd, setTrendingAd] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [categories, setCategories] = useState<Record<string, unknown>[]>([]);

  const categoryLabels = useMemo<Record<ArtCategory, string>>(() => ({
    music: select({ es: 'Música', en: 'Music' }),
    writing: select({ es: 'Escritura', en: 'Writing' }),
    tattoo: select({ es: 'Tatuajes', en: 'Tattoos' }),
    digital_design: select({ es: 'Diseño Digital', en: 'Digital Design' }),
    fashion_design: select({ es: 'Diseño de Moda', en: 'Fashion Design' }),
    photography: select({ es: 'Fotografía', en: 'Photography' }),
    sculpture: select({ es: 'Escultura', en: 'Sculpture' }),
    painting: select({ es: 'Pintura', en: 'Painting' }),
    dance: select({ es: 'Danza', en: 'Dance' }),
    filmmaking: select({ es: 'Cine', en: 'Filmmaking' }),
    makeup: select({ es: 'Maquillaje', en: 'Makeup' }),
  }), [select]);

  useEffect(() => {
    loadHomeData();

    // Listen for new posts created
    const handlePostCreated = () => {
      loadPosts(); // Refresh posts when a new one is created
    };

    window.addEventListener('postCreated', handlePostCreated);

    return () => {
      window.removeEventListener('postCreated', handlePostCreated);
    };
  }, []);

  useEffect(() => {
    const existingAd = posts.find((post) => post?.type === 'ad');
    if (existingAd) {
      setTrendingAd(existingAd);
      return;
    }

    let cancelled = false;
    (async () => {
      const ads = await adsDisplayService.getActiveAdsForUser(user?.id, 1);
      if (cancelled) return;
      setTrendingAd(ads[0] ?? null);
    })();

    return () => {
      cancelled = true;
    };
  }, [posts, user?.id]);

  const loadHomeData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadPosts(),
        loadPopularCategories(),
      ]);
    } catch (err) {
      setError(select({ es: 'Error al cargar los datos', en: 'Error loading data' }));
      console.error('Error loading home data:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadPosts = async () => {
    try {
      console.log('🏠 HomeView: Loading posts with ads...');
      const data = await postsService.getPosts({
        sortBy: 'recent',
        limit: 10,
        includeAds: true
      });
      console.log('🏠 HomeView: Loaded', data.length, 'posts (including ads)');
      setPosts(data);
    } catch (err) {
      console.error('Error loading posts:', err);
    }
  };

  const loadPopularCategories = async () => {
    try {
      const { data } = await supabase
        .from('posts')
        .select(`
          category,
          media_files (url, type)
        `)
        .order('created_at', { ascending: false });

      if (!data) return;

      const categoryStats: Record<string, { count: number; imageUrl?: string }> = {};

      (Object.keys(categoryLabels) as ArtCategory[]).forEach(key => {
        categoryStats[key] = { count: 0 };
      });

      data.forEach(post => {
        if (!categoryStats[post.category]) {
          categoryStats[post.category] = { count: 0 };
        }
        categoryStats[post.category].count++;

        if (!categoryStats[post.category].imageUrl && Array.isArray(post.media_files)) {
          const mediaWithImage = post.media_files.find((file: { type?: string; url?: string }) => file.type === 'image' && file.url);
          if (mediaWithImage?.url) {
            categoryStats[post.category].imageUrl = mediaWithImage.url;
          }
        }
      });

      const categoriesArray = Object.entries(categoryStats)
        .map(([category, stats]) => ({
          key: category,
          name: categoryLabels[category as ArtCategory] || category,
          count: stats.count,
          imageUrl: stats.imageUrl,
        }))
        .filter(category => category.count > 0)
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);

      setCategories(categoriesArray);
    } catch (err) {
      console.error('Error loading categories:', err);
    }
  };

  const handleLike = async (postId: string) => {
    try {
      const isLiked = await postsService.checkIfLiked(postId);
      if (isLiked) {
        await postsService.unlikePost(postId);
      } else {
        await postsService.likePost(postId);
      }
      // Optional: Reload posts if you want server-side counts
      // loadPosts();
    } catch (err) {
      if (!err.message?.includes('already liked')) {
        console.error('Error toggling like:', err);
      }
    }
  };

  const handleSave = (postId: string) => {
    console.log('Saved post:', postId);
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  const getCategoryGradient = (categoryKey: string) => {
    const gradients: Record<string, string> = {
      music: 'from-indigo-500 to-purple-600',
      writing: 'from-emerald-500 to-teal-600',
      tattoo: 'from-red-600 to-slate-800',
      digital_design: 'from-cyan-500 to-blue-600',
      fashion_design: 'from-pink-500 to-rose-600',
      photography: 'from-amber-500 to-orange-600',
      sculpture: 'from-slate-500 to-stone-600',
      painting: 'from-rainbow-gradient',
      dance: 'from-fuchsia-500 to-pink-600',
      filmmaking: 'from-red-500 to-yellow-600',
      makeup: 'from-rose-400 to-pink-500',
    };

    if (categoryKey === 'painting') {
      return 'bg-gradient-to-br from-red-400 via-yellow-400 via-green-400 via-blue-400 to-purple-500';
    }

    return `bg-gradient-to-br ${gradients[categoryKey] || 'from-purple-500 to-pink-600'}`;
  };

  const navigateToExplore = (state?: Record<string, unknown>) => {
    navigate('/explore', { state });
  };

  const handleCategorySelect = (categoryKey: string) => {
    navigateToExplore({ category: categoryKey });
  };

  const renderCategoryCard = (
    category: { key: string; name: string; count: number; imageUrl?: string },
    index: number
  ) => {
    const isTopCategory = index < 3;
    const hasImage = Boolean(category.imageUrl);

    return (
      <button
        key={`${category.key}-${index}`}
        type="button"
        onClick={() => handleCategorySelect(category.key)}
        className="group relative h-[280px] w-56 md:w-64 flex-shrink-0 overflow-hidden rounded-[2rem] transition-all duration-700 hover:-translate-y-2 hover:shadow-[0_20px_50px_-10px_rgba(0,0,0,0.5)] border border-white/20 shadow-[inset_0_0_20px_rgba(255,255,255,0.1)]"
      >
        <div
          className={`absolute inset-0 bg-cover bg-center transition-transform duration-1000 group-hover:scale-110 ${hasImage ? '' : getCategoryGradient(category.key)}`}
          style={hasImage ? { backgroundImage: `url(${category.imageUrl})` } : undefined}
        />

        {/* Liquid Glass Shine Overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-black/30 pointer-events-none mix-blend-overlay" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-90" />

        <div className="relative z-10 flex h-full flex-col justify-end p-6 text-left">
          <div className="transform transition-transform duration-500 group-hover:-translate-y-2">
            <h3 className="text-xl md:text-2xl font-bold text-white mb-1 drop-shadow-lg tracking-tight line-clamp-2 leading-tight">
              {category.name}
            </h3>
            <p className="text-white/80 font-medium text-xs mb-3 flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-green-400 shadow-[0_0_5px_rgba(74,222,128,0.8)]"></span>
              {formatNumber(category.count)} {select({ es: 'obras', en: 'works' })}
            </p>

            <div className="flex items-center gap-3 opacity-0 transform translate-y-4 transition-all duration-500 group-hover:opacity-100 group-hover:translate-y-0">
              <span className="inline-flex items-center gap-2 rounded-full bg-white/25 backdrop-blur-xl px-4 py-1.5 text-xs font-bold text-white border border-white/30 shadow-[0_4px_10px_rgba(0,0,0,0.1)] hover:bg-white/40 transition-colors">
                {select({ es: 'Explorar', en: 'Explore' })} <ArrowRight size={12} />
              </span>
            </div>
          </div>

          {/* Top Badge: Liquid Style */}
          {isTopCategory && (
            <div className="absolute top-4 right-4">
              <span className="inline-flex items-center gap-1 leading-none rounded-full bg-white/30 backdrop-blur-md border border-white/40 px-2.5 py-1 text-[10px] uppercase font-bold text-white shadow-[0_4px_10px_rgba(0,0,0,0.1)] tracking-wider">
                🔥 TOP
              </span>
            </div>
          )}
        </div>
      </button>
    );
  };
  if (loading) {
    return (
      <div className="max-w-7xl mx-auto p-4">
        <div className="animate-pulse space-y-8">
          {/* Stories skeleton */}
          <div className="bg-gray-200 h-24 rounded-xl"></div>

          {/* Header skeleton */}
          <div className="space-y-4">
            <div className="h-8 bg-gray-200 rounded w-1/3"></div>
            <div className="h-4 bg-gray-200 rounded w-2/3"></div>
          </div>

          {/* Stats skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-gray-200 h-32 rounded-xl"></div>
            ))}
          </div>

          {/* Categories skeleton */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
              <div key={i} className="bg-gray-200 h-32 rounded-xl"></div>
            ))}
          </div>

          {/* Posts skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="bg-gray-200 h-96 rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto p-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      </div>
    );
  }

  const trendingPosts = posts.slice().sort((a, b) => (b.views_count ?? b.views ?? 0) - (a.views_count ?? a.views ?? 0));
  const recentPosts = posts.slice().sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  const organicTrending = trendingPosts.filter((post) => post?.type !== 'ad');
  const trendingGridPosts = trendingAd
    ? [...organicTrending.slice(0, 4).filter((post) => post?.id !== trendingAd?.id).slice(0, 3), trendingAd].slice(0, 4)
    : organicTrending.slice(0, 4);

  return (
    <div className="max-w-7xl mx-auto p-4">
      {/* Stories Section */}
      <StoriesSection />

      {/* Hero Section */}
      <div className="relative mb-12 overflow-hidden">
        <div className="bg-gradient-to-br from-purple-600 via-pink-600 to-orange-500 rounded-3xl p-8 md:p-12">
          <div className="relative z-10">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="text-white" size={24} />
              <span className="text-white/90 font-medium">{select({ es: 'Bienvenido a', en: 'Welcome to' })}</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
              SoArtistic
            </h1>
            <p className="text-xl text-white/90 mb-8 max-w-2xl">
              {select({
                es: 'Descubre, conecta y comparte arte con artistas de todo el mundo. Una comunidad donde la creatividad no tiene límites.',
                en: 'Discover, connect, and share art with artists around the world. A community where creativity has no limits.'
              })}
            </p>
            <div className="flex flex-wrap gap-4">
              <button
                onClick={() => {
                  analyticsService.trackEvent('click_explore_art_home');
                  navigate('/explore');
                }}
                className="bg-white text-purple-600 px-6 py-3 rounded-full font-semibold hover:bg-gray-100 transition-all transform hover:scale-105 shadow-lg"
              >
                {select({ es: 'Explorar Arte', en: 'Explore Art' })}
              </button>
              {user ? (
                <button
                  onClick={() => navigate('/profile')}
                  className="border-2 border-white text-white px-6 py-3 rounded-full font-semibold hover:bg-white/10 transition-all"
                >
                  {select({ es: 'Ir a mi perfil', en: 'Go to my profile' })}
                </button>
              ) : (
                <button
                  onClick={() => window.location.href = '/register?type=artist'}
                  className="border-2 border-white text-white px-6 py-3 rounded-full font-semibold hover:bg-white/10 transition-all"
                >
                  {select({ es: 'Únete como Artista', en: 'Join as an Artist' })}
                </button>
              )}
            </div>
          </div>

          {/* Floating elements */}
          <div className="absolute top-8 right-8 w-20 h-20 bg-white/10 rounded-full animate-pulse"></div>
          <div className="absolute bottom-12 right-20 w-12 h-12 bg-white/10 rounded-full animate-bounce delay-300"></div>
          <div className="absolute top-20 left-20 w-8 h-8 bg-white/10 rounded-full animate-ping delay-700"></div>
        </div>
      </div>

      {/* Popular Categories (Infinite Slider) */}
      <div className="mb-20 overflow-hidden">
        <div className="mb-8 flex flex-col items-center text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-3 tracking-tight">
            {select({ es: 'Explora por Categorías', en: 'Explore by Category' })}
          </h2>
          <p className="text-gray-500 max-w-2xl text-lg">
            {select({
              es: 'Sumérgete en estilos únicos y descubre talentos emergentes en cada especialidad.',
              en: 'Dive into unique styles and discover emerging talents in every specialty.'
            })}
          </p>
        </div>

        <div className="relative w-full">
          {/* Gradients for fade effect on edges */}
          <div className="pointer-events-none absolute left-0 top-0 z-10 h-full w-24 bg-gradient-to-r from-slate-50 to-transparent" />
          <div className="pointer-events-none absolute right-0 top-0 z-10 h-full w-24 bg-gradient-to-l from-slate-50 to-transparent" />

          <div className="flex w-max animate-scroll gap-8 py-8 hover:[animation-play-state:paused]">
            {/* Render 3 sets of categories for smooth infinite loop */}
            {[...categories, ...categories, ...categories].map((category, index) => renderCategoryCard(category, index))}
          </div>
        </div>
      </div>

      {/* Trending Posts */}
      <div className="mb-12">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
              <div className="p-2 bg-gradient-to-r from-orange-400 to-pink-500 rounded-xl">
                <TrendingUp size={24} className="text-white" />
              </div>
              {select({ es: 'En Tendencia', en: 'Trending' })}
            </h2>
            <p className="text-gray-600">{select({ es: 'Las obras más populares del momento', en: 'The most popular pieces right now' })}</p>
          </div>
          <button
            onClick={() => navigateToExplore()}
            className="flex items-center gap-2 text-purple-600 hover:text-purple-700 font-medium"
          >
            {select({ es: 'Ver más', en: 'See more' })} <ArrowRight size={20} />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {trendingGridPosts.map((post) => (
            <PostCard
              key={post.id}
              post={{
                ...post,
                type: post.type || 'post',
                author: post.profiles || post.author || {
                  id: post.author_id || '',
                  username: select({ es: 'usuario_desconocido', en: 'unknown_user' }),
                  full_name: select({ es: 'Usuario Desconocido', en: 'Unknown User' }),
                  avatar_url: null,
                  is_premium: false
                },
                media: post.media_files || post.media || [],
                comments: post.comments || [],
                likes: post.likes_count || post.likes || 0,
                views: post.views_count || post.views || 0,
                createdAt: post.created_at || post.createdAt
              }}
              onLike={handleLike}
              onSave={handleSave}
            />
          ))}
        </div>
      </div>

      {/* Recent Posts */}
      <div className="mb-12">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
              <div className="p-2 bg-gradient-to-r from-blue-400 to-purple-500 rounded-xl">
                <Clock size={24} className="text-white" />
              </div>
              {select({ es: 'Recientes', en: 'Recent' })}
            </h2>
            <p className="text-gray-600">{select({ es: 'Las últimas creaciones de la comunidad', en: 'The latest creations from the community' })}</p>
          </div>
          <button
            onClick={() => navigateToExplore()}
            className="flex items-center gap-2 text-purple-600 hover:text-purple-700 font-medium"
          >
            {select({ es: 'Ver más', en: 'See more' })} <ArrowRight size={20} />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {recentPosts.slice(0, 4).map((post) => (
            <PostCard
              key={post.id}
              post={{
                ...post,
                type: post.type || 'post',
                author: post.profiles || post.author || {
                  id: post.author_id || '',
                  username: select({ es: 'usuario_desconocido', en: 'unknown_user' }),
                  full_name: select({ es: 'Usuario Desconocido', en: 'Unknown User' }),
                  avatar_url: null,
                  is_premium: false
                },
                media: post.media_files || post.media || [],
                comments: post.comments || [],
                likes: post.likes_count || post.likes || 0,
                views: post.views_count || post.views || 0,
                createdAt: post.created_at || post.createdAt
              }}
              onLike={handleLike}
              onSave={handleSave}
            />
          ))}
        </div>
      </div>

      {/* CTA Section */}
      <div className="relative rounded-3xl overflow-hidden mb-8">
        <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 p-8 md:p-12">
          <div className="text-center relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {select({ es: '¿Listo para mostrar tu arte?', en: 'Ready to showcase your art?' })}
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              {select({ es: 'Únete a miles de artistas que ya comparten su creatividad en SoArtistic', en: 'Join thousands of artists already sharing their creativity on SoArtistic' })}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => window.location.href = '/register?type=artist'}
                className="bg-white text-purple-600 px-8 py-4 rounded-full font-bold hover:bg-gray-100 transition-all transform hover:scale-105 shadow-lg"
              >
                {select({ es: 'Crear mi cuenta de artista', en: 'Create my artist account' })}
              </button>
              <button
                onClick={() => navigateToExplore()}
                className="border-2 border-white text-white px-8 py-4 rounded-full font-bold hover:bg-white/10 transition-all"
              >
                {select({ es: 'Explorar la comunidad', en: 'Explore the community' })}
              </button>
            </div>
          </div>

          {/* Background patterns */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-10 left-10 w-32 h-32 border border-white/30 rounded-full"></div>
            <div className="absolute bottom-10 right-10 w-24 h-24 border border-white/30 rounded-full"></div>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-40 h-40 border border-white/20 rounded-full"></div>
          </div>
        </div>
      </div>

      {/* Social Media Section */}
      <div className="flex flex-col items-center justify-center py-8 border-t border-gray-100">
        <h3 className="text-xl font-semibold text-gray-800 mb-6">
          {select({ es: 'Síguenos en nuestras redes', en: 'Follow us on social media' })}
        </h3>
        <div className="flex gap-6">
          <a
            href="https://www.instagram.com/soartisticarte/"
            target="_blank"
            rel="noopener noreferrer"
            className="p-3 bg-pink-100 text-pink-600 rounded-full hover:bg-pink-200 hover:scale-110 transition-all duration-300"
            aria-label="Instagram"
          >
            <Instagram size={24} />
          </a>
          <a
            href="https://www.facebook.com/people/Soartistic-Soartistic/pfbid073MyHSXBTRaERzH66nBvXXfmS22rxkKWkiaxY9aAeoEGEVAzp84zbmE2TUfB2kARl/?sk=about"
            target="_blank"
            rel="noopener noreferrer"
            className="p-3 bg-blue-100 text-blue-600 rounded-full hover:bg-blue-200 hover:scale-110 transition-all duration-300"
            aria-label="Facebook"
          >
            <Facebook size={24} />
          </a>
          <a
            href="https://x.com/SoartisticArt"
            target="_blank"
            rel="noopener noreferrer"
            className="p-3 bg-gray-100 text-gray-800 rounded-full hover:bg-gray-200 hover:scale-110 transition-all duration-300"
            aria-label="X (Twitter)"
          >
            <Twitter size={24} />
          </a>
          <a
            href="https://www.linkedin.com/company/107366234/admin/page-posts/published/?shareMsgArgs=null"
            target="_blank"
            rel="noopener noreferrer"
            className="p-3 bg-blue-50 text-blue-700 rounded-full hover:bg-blue-100 hover:scale-110 transition-all duration-300"
            aria-label="LinkedIn"
          >
            <Linkedin size={24} />
          </a>
        </div>
      </div>
    </div>
  );
};

export default HomeView;
